import React from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatedSection } from '../components/animated/AnimatedSection';
import { MagneticButton } from '../components/animated/MagneticButton';
import { CurvedPhotoGallery } from '../components/animated/CurvedPhotoGallery';
import { Users, Heart, TrendingUp, Award, Target, Zap } from 'lucide-react';
export function SocialImpactPage() {
  const navigate = useNavigate();
  const impactPhotos = [{
    url: 'https://uploadthingy.s3.us-west-1.amazonaws.com/fieYNmErKpAxE9njqoZfbs/IMG_20251023_125324.jpg',
    alt: 'Leadership Training Session'
  }, {
    url: 'https://uploadthingy.s3.us-west-1.amazonaws.com/tNcsU3kySKAjPSzKUSfQ7D/IMG_20251023_125626.jpg',
    alt: 'Professional Development'
  }, {
    url: 'https://uploadthingy.s3.us-west-1.amazonaws.com/ghhzDMnxYyJEs2QNeZXGWC/1755202594528.jpg',
    alt: 'Youth Empowerment'
  }, {
    url: 'https://uploadthingy.s3.us-west-1.amazonaws.com/muHHmEBKQ1boiHjLaMty3n/IMG_6586.jpg',
    alt: 'Skills Development'
  }];
  const whyChooseUs = [{
    icon: <Award className="w-8 h-8 text-blue-600" data-id="element-1640" />,
    title: 'We deliver excellence consistently',
    description: 'Our track record speaks for itself with proven results across multiple projects.'
  }, {
    icon: <TrendingUp className="w-8 h-8 text-green-600" data-id="element-1641" />,
    title: 'Clients usually see real improvements in under a year',
    description: 'Fast, measurable impact that transforms organizations and individuals.'
  }, {
    icon: <Target className="w-8 h-8 text-blue-600" data-id="element-1642" />,
    title: 'You only pay for what you need no ineffective contracts',
    description: 'Flexible, transparent pricing with no hidden costs or long-term commitments.'
  }, {
    icon: <Zap className="w-8 h-8 text-green-600" data-id="element-1643" />,
    title: 'Innovative solutions for people problem',
    description: 'Creative approaches to complex challenges in talent and organizational development.'
  }, {
    icon: <Heart className="w-8 h-8 text-blue-600" data-id="element-1644" />,
    title: "Our project helps fund free training for young professionals who wouldn't otherwise get it.",
    description: 'Every project you invest in with us directly supports youth development programs, giving underprivileged children access to quality training and mentorship.'
  }];
  return <div className="flex flex-col min-h-screen bg-white" data-id="element-1645">
      <main className="flex-grow pt-20" data-id="element-1646">
        {/* Hero Section */}
        <section className="relative py-32 overflow-hidden" data-id="element-1647">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-white to-green-50" data-id="element-1648" />
          <div className="absolute top-20 right-10 w-96 h-96 bg-green-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20" data-id="element-1649" />

          <div className="container mx-auto px-6 lg:px-8 relative z-10" data-id="element-1650">
            <div className="max-w-4xl mx-auto text-center" data-id="element-1651">
              <h1 className="text-5xl md:text-7xl font-bold tracking-tight text-gray-900 mb-8" data-id="element-1652">
                Social{' '}
                <span className="bg-gradient-to-r from-blue-600 to-green-600 bg-clip-text text-transparent" data-id="element-1653">
                  Impact
                </span>
              </h1>
              <p className="text-xl md:text-2xl text-gray-600 leading-relaxed max-w-3xl mx-auto" data-id="element-1654">
                SkillzHQ is more than a business, we are a catalyst for
                development. We run mentorship and skills programmes for young
                people to improve their employability skills. When you work with
                us, part of what you pay goes straight into giving them that
                chance.
              </p>
            </div>
          </div>
        </section>

        {/* Curved Photo Gallery */}
        <CurvedPhotoGallery photos={impactPhotos} title="Our Impact in Action" subtitle="Transforming lives through hands-on training and community development" data-id="element-1655" />

        {/* Youth Engagement Section */}
        <AnimatedSection className="py-24 bg-gradient-to-b from-gray-50 to-white" data-id="element-1656">
          <div className="container mx-auto px-6 lg:px-8" data-id="element-1657">
            <div className="max-w-6xl mx-auto" data-id="element-1658">
              <div className="text-center mb-16" data-id="element-1659">
                <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6" data-id="element-1660">
                  Youth Engagement
                </h2>
                <p className="text-xl text-gray-600 max-w-3xl mx-auto" data-id="element-1661">
                  We design programmes that empower Africa's youth with the
                  skills, confidence, and opportunities to thrive.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mb-16" data-id="element-1662">
                <div className="bg-gradient-to-br from-blue-50 to-white rounded-3xl p-8 border-2 border-blue-100" data-id="element-1663">
                  <div className="flex items-center gap-4 mb-6" data-id="element-1664">
                    <div className="p-3 bg-blue-100 rounded-2xl" data-id="element-1665">
                      <Users className="w-8 h-8 text-blue-600" data-id="element-1666" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900" data-id="element-1667">
                      Mentorship Programme
                    </h3>
                  </div>
                  <p className="text-gray-700 leading-relaxed" data-id="element-1668">
                    Matching young talents with experienced leaders who guide,
                    inspire, and support career and personal growth.
                  </p>
                </div>

                <div className="bg-gradient-to-br from-green-50 to-white rounded-3xl p-8 border-2 border-green-100" data-id="element-1669">
                  <div className="flex items-center gap-4 mb-6" data-id="element-1670">
                    <div className="p-3 bg-green-100 rounded-2xl" data-id="element-1671">
                      <Award className="w-8 h-8 text-green-600" data-id="element-1672" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900" data-id="element-1673">
                      Skills Development
                    </h3>
                  </div>
                  <p className="text-gray-700 leading-relaxed" data-id="element-1674">
                    From unemployed youth to budding entrepreneurs, we equip
                    thousands with market-relevant skills that unlock economic
                    opportunity.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </AnimatedSection>

        {/* Why Teams Keep Coming Back */}
        <AnimatedSection className="py-24 bg-white" data-id="element-1675">
          <div className="container mx-auto px-6 lg:px-8" data-id="element-1676">
            <div className="max-w-6xl mx-auto" data-id="element-1677">
              <div className="text-center mb-16" data-id="element-1678">
                <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6" data-id="element-1679">
                  Why teams keep coming back to us
                </h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-1680">
                {whyChooseUs.map((item, index) => <div key={index} className="bg-white rounded-2xl p-6 border-2 border-gray-100 hover:border-blue-300 hover:shadow-xl transition-all duration-300" data-id="element-1681">
                    <div className="flex justify-center mb-4" data-id="element-1682">{item.icon}</div>
                    <h3 className="text-lg font-bold text-gray-900 mb-3 text-center" data-id="element-1683">
                      {item.title}
                    </h3>
                    <p className="text-gray-600 text-center leading-relaxed text-sm" data-id="element-1684">
                      {item.description}
                    </p>
                  </div>)}
              </div>
            </div>
          </div>
        </AnimatedSection>

        {/* CTA Section */}
        <AnimatedSection className="py-32 bg-gradient-to-br from-blue-600 to-green-600 relative overflow-hidden" data-id="element-1685">
          <div className="absolute inset-0 bg-grid-white/[0.05] bg-[size:32px_32px]" data-id="element-1686" />
          <div className="container mx-auto px-6 lg:px-8 relative z-10" data-id="element-1687">
            <div className="max-w-4xl mx-auto text-center" data-id="element-1688">
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-8" data-id="element-1689">
                Let's talk
              </h2>
              <p className="text-xl text-blue-100 mb-12 max-w-2xl mx-auto leading-relaxed" data-id="element-1690">
                Grab a 30-minute call with us. We'll look at where you are, spot
                the couple of things that'll move the needle fastest, and give
                you a simple plan you can run with – whether you hire us or not.
              </p>
              <MagneticButton variant="secondary" size="lg" onClick={() => navigate('/contact')} data-id="element-1691">
                Book a 30-minute call →
              </MagneticButton>
              <p className="text-sm text-blue-100 mt-6" data-id="element-1692">
                (We only open 10 of these slots a week, so if you're ready, grab
                one now.)
              </p>
              <p className="text-lg text-white mt-8" data-id="element-1693">
                Looking forward to chatting,
                <br data-id="element-1694" />
                <span className="font-semibold" data-id="element-1695">The SkillzHQ team</span>
              </p>
            </div>
          </div>
        </AnimatedSection>
      </main>
    </div>;
}